// src/pages/locations/flash.ts
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * One-shot confirmation carried through the redirect that follows a save, so
 * the user lands back on the list with proof the record went through.
 */
export type LocationsFlash = { message: string };

const KEY = "locationsFlash";

/** Router state for `navigate(path, withFlash("City created."))`. */
export function withFlash(message: string) {
  return { state: { [KEY]: { message } satisfies LocationsFlash } };
}

/** Reads the message once, then strips it so a refresh does not repeat it. */
export function useConsumeFlash(): string | null {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as Record<string, unknown> | null;
  const incoming =
    state && typeof state === "object"
      ? (state[KEY] as LocationsFlash | undefined)?.message ?? null
      : null;

  const [message] = React.useState<string | null>(incoming);

  React.useEffect(() => {
    if (!incoming) return;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    // Runs once per arrival: the redirect below clears `incoming`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  return message;
}
